/**
 * useInspectors — tenant-scoped read of `v9_inspectors`.
 *
 * RLS handles the scoping server-side, but we also pass an explicit
 * `.eq('tenant_id', tenant.id)` so a misconfigured policy still can't leak
 * other tenants' inspectors. Cheap defence in depth.
 *
 * Cached at module scope per tenant — the inspector list is small (typically
 * <30 rows) and changes via the Settings page, so a 60s TTL is fine.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { getSupabase } from '../lib/supabase';
import { useTenant } from '../contexts/TenantContext';
import type { Inspector } from '../types';

interface InspectorRow {
  id: string;
  tenant_id: string;
  crm_user_id: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  region_code: string | null;
  is_active: boolean;
  config: Record<string, unknown> | null;
}

interface CacheEntry {
  ts: number;
  data: Inspector[];
}

const CACHE_TTL_MS = 60_000;
const cache = new Map<string, CacheEntry>();

interface UseInspectorsReturn {
  inspectors: Inspector[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

function toInspector(r: InspectorRow): Inspector {
  return {
    id: r.id,
    tenant_id: r.tenant_id,
    crm_user_id: r.crm_user_id,
    name: r.name,
    email: r.email,
    phone: r.phone,
    region_code: r.region_code,
    is_active: r.is_active,
    config: r.config ?? {},
  };
}

export function useInspectors(): UseInspectorsReturn {
  const { tenant } = useTenant();
  const tenantId = tenant?.id ?? null;

  const [inspectors, setInspectors] = useState<Inspector[]>(() => {
    if (!tenantId) return [];
    const hit = cache.get(tenantId);
    if (hit && Date.now() - hit.ts < CACHE_TTL_MS) return hit.data;
    return [];
  });
  const [loading, setLoading] = useState<boolean>(!!tenantId);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    const supabase = getSupabase();
    const { data, error: err } = await supabase
      .from('v9_inspectors')
      .select('id, tenant_id, crm_user_id, name, email, phone, region_code, is_active, config')
      // Explicit tenant filter — RLS handles this too, but belt-and-braces.
      .eq('tenant_id', tenantId)
      .order('name', { ascending: true });

    if (err) {
      setError(err.message);
      setInspectors([]);
    } else {
      const mapped = (data ?? []).map((r) => toInspector(r as InspectorRow));
      setInspectors(mapped);
      cache.set(tenantId, { ts: Date.now(), data: mapped });
    }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    const hit = cache.get(tenantId);
    if (hit && Date.now() - hit.ts < CACHE_TTL_MS) {
      setInspectors(hit.data);
      setLoading(false);
      return;
    }
    void refresh();
  }, [tenantId, refresh]);

  const activeInspectors = useMemo(() => inspectors.filter((i) => i.is_active), [inspectors]);

  return {
    inspectors: activeInspectors,
    loading,
    error,
    refresh,
  };
}

/** Test helper — clear the entire inspectors cache. */
export function clearInspectorsCache(): void {
  cache.clear();
}
